import React from 'react';

import { TextField } from 'Containers/App/types';
import { RichTextContent } from 'ContentfulDefaults/types/_shared';
import { ContentfulInfoBar } from 'ContentfulWrappers/ContentfulInfoBar';
import { useTextContext } from 'Contexts/textContext';
import { IconTypes, Paragraph, RadioPseudo } from 'DesignLibrary/atoms';
import { useViewport } from 'DesignLibrary/context';
import RichText from 'Shared/RichText';
import Text from 'Shared/Text';
import { EducationalDrawerInfoTypes, SAVINGS_ACCOUNT_TYPES } from 'Types/entities';
import { FsaBanner, toDollars } from 'Utils/helpers';

import { EligContainer, EligBarStyling } from './styled';

interface EligBarProps {
  isSelected: boolean;
  field: string;
  headerVars?: Record<string, unknown>;
  textVars?: Array<any>;
}

type CustomMessage = {
  color?: 'green' | 'yellow' | 'blue' | 'purple';
  icon?: IconTypes;
  text: RichTextContent;
  header: string;
};

const EligBar = ({ isSelected, field, headerVars = {}, textVars = [] }: EligBarProps) => {
  const { device } = useViewport();
  const { retrieveContentfulData } = useTextContext();

  const customMessage = retrieveContentfulData<CustomMessage>(field);

  const color = customMessage?.color || 'blue';

  return (
    <EligBarStyling color={color} isSelected={isSelected}>
      <RadioPseudo
        sizing={device === 'mobile' ? 'small' : 'normal'}
        color={`--primary-${color}`}
        isChecked={isSelected}
        iconOverride={customMessage?.icon}
      />
      <div>
        <Paragraph weight="bold" color="--text-black">
          <Text field={`${field}.header` as TextField} vars={headerVars} />
        </Paragraph>
        <RichText
          useStylingWrapper
          richTextStyling={{ p: { size: 'small' } }}
          inlineParagraphs
          field={`${field}.text` as TextField}
          vars={textVars}
        />
      </div>
    </EligBarStyling>
  );
};

interface EligBarsProps {
  infoType: EducationalDrawerInfoTypes;
  isSelectedEligible: boolean;
  employerContribution: number | null;
  fsaBanner?: FsaBanner;
}

export const EligBars = ({
  infoType,
  isSelectedEligible,
  employerContribution,
  fsaBanner,
}: EligBarsProps) => {
  const prefix = `health_section.secondary_content.${infoType}_modal`;
  const eligiblePlanKey = `${prefix}.eligible_plan_message`;
  const nonEligiblePlanKey = `${prefix}.non_eligible_plan_message`;

  return (
    <EligContainer>
      {infoType !== SAVINGS_ACCOUNT_TYPES.FSA &&
        (isSelectedEligible ? (
          <EligBar isSelected field={eligiblePlanKey} />
        ) : (
          <EligBar isSelected={false} field={nonEligiblePlanKey} />
        ))}
      {infoType !== SAVINGS_ACCOUNT_TYPES.FSA &&
        !!employerContribution &&
        (isSelectedEligible ? (
          <EligBar
            isSelected
            field={`${prefix}.employer_contribution_selected_plan_message`}
            headerVars={{ x: toDollars(employerContribution) }}
          />
        ) : (
          <EligBar
            isSelected
            field={`${prefix}.employer_contribution_no_selected_plan_message`}
            headerVars={{ x: toDollars(employerContribution) }}
          />
        ))}

      {/* No banner renders when fsaBanner is 'none' or omitted - this is intentional. */}
      {infoType === SAVINGS_ACCOUNT_TYPES.FSA && fsaBanner === 'eligible' && (
        <EligBar isSelected field={eligiblePlanKey} />
      )}
      {infoType === SAVINGS_ACCOUNT_TYPES.FSA && fsaBanner === 'limited_purpose' && (
        <EligBar isSelected field={nonEligiblePlanKey} />
      )}

      <ContentfulInfoBar field={`${prefix}.custom_alert_message`} />
    </EligContainer>
  );
};
